import type { Tag } from '@/types/tags';
import { cn } from '@/lib/utils';

// A fixed 10-color palette keyed on the tag.color string persisted server-side.
// Values match the seed colors in migrations/019_user_tags_and_crm.sql.
const colorStyles: Record<string, string> = {
  gray: 'bg-muted text-muted-foreground border-border',
  blue: 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/40 dark:text-blue-200',
  teal: 'bg-teal-100 text-teal-800 border-teal-200 dark:bg-teal-900/40 dark:text-teal-200',
  green: 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/40 dark:text-green-200',
  emerald: 'bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-200',
  amber: 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/40 dark:text-amber-200',
  orange: 'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/40 dark:text-orange-200',
  violet: 'bg-violet-100 text-violet-800 border-violet-200 dark:bg-violet-900/40 dark:text-violet-200',
  red: 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/40 dark:text-red-200',
  pink: 'bg-pink-100 text-pink-800 border-pink-200 dark:bg-pink-900/40 dark:text-pink-200',
};

interface TagBadgeProps {
  tag: Pick<Tag, 'name' | 'color'>;
  onRemove?: () => void;
  className?: string;
  title?: string;
}

export function TagBadge({ tag, onRemove, className, title }: TagBadgeProps) {
  const style = colorStyles[tag.color] ?? colorStyles.gray;
  return (
    <span
      title={title ?? tag.name}
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium',
        style,
        className,
      )}
    >
      {tag.name}
      {onRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="ml-1 opacity-60 hover:opacity-100"
          aria-label={`Remove ${tag.name}`}
        >
          ×
        </button>
      )}
    </span>
  );
}
